<template>
  <div
    class="configuration-rule-attribute-values-component attributes-table-wrapper"
    :class="{ expanded: tableExpanded, expandable: otherRules.length > 0 }"
  >
    <el-table :data="classAttributes" stripe style="width: 100%" class="attributes-table">
      <el-table-column prop="displayName" label="Attribute name" fixed width="200" />

      <!-- Editable Rule -->
      <el-table-column fixed width="200">
        <template #header></template>
        <template #default="scope">
          <VersatileFormField
            :prop="scope.row.technicalName"
            :type="scope.row.formFieldType"
            :model-value="currentRuleValues[scope.row.technicalName]"
            :choices="(scope.row.availableValues || []).map((o) => ({ label: o, value: o }))"
            :auto-size="{ minRows: 1, maxRows: 6 }"
            inline
            clearable
            @update:model-value="(value) => setAttributeValue(scope.row.technicalName, value)"
          />
        </template>
      </el-table-column>

      <!-- Default Values Column -->
      <el-table-column v-if="displayDefaultValues" min-width="300">
        <template #header>
          <div>
            <div class="rule-segment-name">Default values</div>
          </div>
        </template>
        <template #default="scope">
          <div class="rule-value">
            <span v-if="scope.row.isArrayType" class="attribute-tags">
              <span
                v-for="(item, index) in defaultValues[scope.row.technicalName] || []"
                :key="`${scope.row.technicalName}-default-tags-${index}`"
              >
                {{ item }}
              </span>
            </span>
            <JsonEditor
              v-else-if="scope.row.type === 'json'"
              :model-value="defaultValues[scope.row.technicalName]"
              readonly
            />
            <span v-else class="attribute-value">
              {{ defaultValues[scope.row.technicalName] }}
            </span>
          </div>
        </template>
      </el-table-column>

      <!-- Rest of the rules columns -->
      <el-table-column
        v-for="rule in otherRules"
        :key="rule.id"
        :min-width="rule.segment.name.length * 10 + 20 > 300 ? `${rule.segment.name.length * 10 + 20}px` : '300px'"
      >
        <template #header>
          <div>
            <div class="rule-segment-name">{{ rule.segment.name }}</div>
            <PlatformGameVersionsLabel :value="rule.platform" />
          </div>
        </template>
        <template #default="scope">
          <div class="rule-value">
            <span v-if="scope.row.isArrayType" class="attribute-tags">
              <span
                v-for="(item, index) in otherRulesValues[rule.id][scope.row.technicalName] || []"
                :key="`${scope.row.technicalName}-${rule.id}-tags-${index}`"
              >
                {{ item }}
              </span>
            </span>
            <JsonEditor
              v-else-if="scope.row.type === 'json'"
              :model-value="otherRulesValues[rule.id][scope.row.technicalName]"
              readonly
            />
            <span v-else class="attribute-value">
              {{ otherRulesValues[rule.id][scope.row.technicalName] }}
            </span>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <div class="expand-button" @click="expandTable">
      <VdTooltip
        :content="!tableExpanded ? 'Expand comparison values' : 'Collapse comparison values'"
        :orientation="!tableExpanded ? 'right' : 'left'"
      >
        <VdIcon store="fa" :name="tableExpanded ? 'chevron-left' : 'chevron-right'" size="small" />
      </VdTooltip>
    </div>
  </div>
</template>

<script lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdTooltip } from "@voodoo.io/voodoo-ui";
import { computed, defineComponent, PropType } from "vue";

import VersatileFormField from "@/components/structure/VersatileFormField/VersatileFormField.vue";
import {
  AttributeValue,
  ConfigurationRuleFormPageConfigurationClassAttributeFragment,
  ConfigurationRuleFormPageConfigurationClassInstanceFragment,
  ConfigurationRuleFormPageRuleFragment,
} from "@/graphql";
import useToggle from "@/hooks/toggle";
import PlatformGameVersionsLabel from "@/pages/games/GameDisplay/children/configurations/components/PlatformGameVersionsLabel.vue";

export default defineComponent({
  components: {
    VersatileFormField,
    VdTooltip,
    VdIcon,
    PlatformGameVersionsLabel,
  },

  props: {
    currentRule: {
      type: Object as PropType<ConfigurationRuleFormPageRuleFragment>,
      required: false,
      default: () => null,
    },

    otherRules: {
      type: Array as PropType<ConfigurationRuleFormPageRuleFragment[]>,
      required: true,
    },

    classAttributes: {
      type: Array as PropType<ConfigurationRuleFormPageConfigurationClassAttributeFragment[]>,
      required: true,
    },

    classInstances: {
      type: Array as PropType<ConfigurationRuleFormPageConfigurationClassInstanceFragment[]>,
      required: true,
    },

    displayDefaultValues: {
      type: Boolean as PropType<boolean>,
      required: true,
    },

    instanceId: {
      type: String,
      required: true,
    },
  },

  emits: ["attribute-value-changed"],

  setup(props, { emit }) {
    const { state: tableExpanded, toggle: expandTable } = useToggle(false);

    const setAttributeValue = (technicalName: string, value: AttributeValue) => {
      emit("attribute-value-changed", { technicalName, value });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const defaultValues = computed<Record<string, any>>(() => {
      const classInstance = props.classInstances.find((instance) => instance.id === props.instanceId);
      if (!classInstance) {
        throw new Error(`unknown class instance ${props.instanceId}`);
      }

      return Object.fromEntries(
        classInstance.values.map(({ technicalName, defaultValue }) => [technicalName, defaultValue])
      );
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentRuleValues = computed<Record<string, any>>(() => {
      if (!props.currentRule) {
        return { ...defaultValues.value };
      }
      const currentRuleInstance = props.currentRule.instances.find((instance) => instance.id === props.instanceId);
      if (currentRuleInstance) {
        const res = Object.fromEntries(
          currentRuleInstance.values.map(({ technicalName, value }) => [technicalName, value])
        );
        return res;
      }
      return { ...defaultValues.value };
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const otherRulesValues = computed<{ [key: string]: Record<string, any> }>(() =>
      props.otherRules.reduce((acc, rule) => {
        const ruleInstance = rule.instances.find((instance) => instance.id === props.instanceId);
        let values = { ...defaultValues.value };

        if (ruleInstance) {
          values = Object.fromEntries(ruleInstance.values.map(({ technicalName, value }) => [technicalName, value]));
        }

        return {
          ...acc,
          [rule.id]: values,
        };
      }, {})
    );

    return {
      expandTable,
      tableExpanded,
      setAttributeValue,
      defaultValues,
      currentRuleValues,
      otherRulesValues,
    };
  },
});
</script>

<style lang="scss">
.configuration-rule-attribute-values-component {
  &.attributes-table-wrapper {
    display: flex;
    width: 100%;
    transition: width 0.25s ease-in-out;

    .expand-button .vd-icon {
      margin-left: 4px;
    }

    /* Class with only one attribute */
    &:not(.expandable) {
      width: 100%;

      .expand-button {
        display: none;
      }
    }

    /* No right border if the table is expandable (because the button will be there) */
    &.expandable {
      .attributes-table {
        border-right: 0px solid transparent;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      .expand-button {
        display: flex;
        align-items: center;
        cursor: pointer;

        width: 20px;

        box-sizing: border-box;
        border: 1px solid $cloudy-blue;
        border-bottom-width: 2px;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;

        &:hover {
          background-color: $cloudy-blue;

          .custom-icon-component {
            fill: $intense-blue;
          }
        }
      }

      /* Closed state */
      &:not(.expanded) {
        width: 720px;
      }
    }
  }

  .attributes-table {
    @extend %card;
    .vd-form-field {
      .el-select {
        width: 100%;
      }
    }
  }

  .rule-value {
    overflow-y: scroll;
    max-height: 150px;
    word-break: break-word;
    white-space: break-spaces;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
